import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';

export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getProjects();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchProjectById = createAsyncThunk(
  'projects/fetchProjectById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.getProject(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createProject = createAsyncThunk(
  'projects/createProject',
  async (project, { rejectWithValue }) => {
    try {
      const response = await apiService.createProject(project);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async ({ id, project }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateProject(id, project);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.deleteProject(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createComment = createAsyncThunk(
  'projects/createComment',
  async (comment, { rejectWithValue }) => {
    try {
      const response = await apiService.createComment(comment);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createPayment = createAsyncThunk(
  'projects/createPayment',
  async (payment, { rejectWithValue }) => {
    try {
      const response = await apiService.createPayment(payment);
      return response.data;
    } catch (error) {
        console.error("Payment creation error:", error.response?.data || error.message);
        return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  projects: [],
  currentProject: null,
  isLoading: false,
  error: null
};

const projectsSlice = createSlice({
    name: 'projects',
    initialState,
    reducers: {
      clearError: (state) => {
        state.error = null;
      },
      clearCurrentProject: (state) => {
        state.currentProject = null;
      }
    },
    extraReducers: (builder) => {
      builder
        .addCase(fetchProjects.pending, (state) => {
          state.isLoading = true;
          state.error = null;
        })
        .addCase(fetchProjects.fulfilled, (state, action) => {
          state.isLoading = false;
          state.projects = action.payload;
        })
        .addCase(fetchProjects.rejected, (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        })
        .addCase(fetchProjectById.pending, (state) => {
          state.isLoading = true;
          state.error = null;
        })
        .addCase(fetchProjectById.fulfilled, (state, action) => {
          state.isLoading = false;
          state.currentProject = action.payload;
        })
        .addCase(fetchProjectById.rejected, (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        })
        .addCase(createProject.pending, (state) => {
          state.isLoading = true;
          state.error = null;
        })
        .addCase(createProject.fulfilled, (state, action) => {
          state.isLoading = false;
          state.projects.push(action.payload);
        })
        .addCase(createProject.rejected, (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        })
        .addCase(updateProject.pending, (state) => {
          state.isLoading = true;
          state.error = null;
        })
        .addCase(updateProject.fulfilled, (state, action) => {
          state.isLoading = false;
          state.currentProject = action.payload;
          state.projects = state.projects.map(project => 
            project.id === action.payload.id ? action.payload : project
          );
        })
        .addCase(updateProject.rejected, (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        })
        .addCase(deleteProject.pending, (state) => {
          state.isLoading = true;
          state.error = null;
        })
        .addCase(deleteProject.fulfilled, (state, action) => {
          state.isLoading = false;
          state.projects = state.projects.filter(project => project.id !== action.payload);
        })
        .addCase(deleteProject.rejected, (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        })
        .addCase(createComment.fulfilled, (state, action) => {
          if (state.currentProject) {
            state.currentProject.comments.push(action.payload);
          }
        })
        .addCase(createPayment.fulfilled, (state, action) => {
          if (state.currentProject) {
            state.currentProject.payments.push(action.payload);
            // Recalculate total_paid
            const totalPaid = state.currentProject.payments.reduce(
              (sum, payment) => sum + parseFloat(payment.amount), 0
            );
            state.currentProject.total_paid = totalPaid;
            state.currentProject.remaining_amount = 
              parseFloat(state.currentProject.quoted_amount) - totalPaid;
          }
        });
    }
  });

export const { clearError, clearCurrentProject } = projectsSlice.actions;
export default projectsSlice.reducer;